import loggerService from '../logger.service';
import { capDiagnosticString } from '../diagnostics';

// Referenced by both the throttle condition and its log line, so the two cannot drift apart.
const RATE_LIMIT_CALLS_PER_10_SEC = 50;
const RATE_LIMIT_DELAY_MS = 2000;
interface Client {
  get: Function;
  post: Function;
  put: Function;
  patch: Function;
  delete: Function;
  baseUrl: string;
  authToken: string;
}

interface ApiResponse<T> {
  statusCode: Number;
  statusText: string;
  data: T;
}

interface ApiAdditionalConfigs {
  useGivenUrl: boolean;
  addTokenToBody?: string;
  addTokenToBodyItself?: Boolean;
}

export default class ApiClient implements Client {
  baseUrl: string;

  protected maxAuthRetries: number = 0;

  private maxFailedFetchRetries: number = 2;

  private lastTimeStamp = 0;
  private callsInLast10Sec = 0;
  private rateLimitedCalls: Record<string, any> = {};

  constructor(baseUrl: string) {
    this.lastTimeStamp = Date.now();
    this.baseUrl = baseUrl;
  }

  onUnauthorized(forced: boolean = false, error?: string): Promise<unknown> {
    return Promise.resolve();
  }

  get(url: string, params?: Object, options?: Object, config?: ApiAdditionalConfigs): Promise<ApiResponse<any>> {
    return this.sendForJson(this.formUrl(url, params), undefined, options);
  }

  post(url: string, body?: Object, options?: any, config?: ApiAdditionalConfigs): Promise<ApiResponse<any>> {
    return this.sendForJson(this.absoluteUrl(url), body, {
      ...options,
      method: 'POST'
    });
  }

  put(url: string, body?: any, options?: any, config?: ApiAdditionalConfigs): Promise<ApiResponse<any>> {
    if (config?.useGivenUrl) {
      if (config?.addTokenToBodyItself && body) {
        (body as any).token = this.authToken;
      } else if (config?.addTokenToBody && body) {
        (body as any)[config.addTokenToBody].token = this.authToken;
      }
      return this.sendForJson(url, body, {
        ...options,
        method: 'PUT'
      });
    }
    return this.sendForJson(this.absoluteUrl(url), body, {
      ...options,
      method: 'PUT'
    });
  }

  patch(url: string, body?: Object, options?: any, config?: ApiAdditionalConfigs): Promise<ApiResponse<any>> {
    return this.sendForJson(this.absoluteUrl(url), body, {
      ...options,
      method: 'PATCH'
    });
  }

  delete(url: string, body?: Object, options?: any, config?: ApiAdditionalConfigs): Promise<ApiResponse<any>> {
    return this.sendForJson(this.absoluteUrl(url), body, {
      ...options,
      method: 'DELETE'
    });
  }

  get authToken(): string {
    return '';
  }

  get trailingSlash(): string {
    return '/';
  }

  private formUrl(url: string, query?: Object): string {
    const params: any = query || {};
    return Object.keys(params).reduce(
      (acc: URL, key) => {
        acc.searchParams.set(key, params[key]);
        return acc;
      },
      new URL(this.absoluteUrl(url))
    ).href;
  }

  private absoluteUrl(url: string): string {
    return `${this.baseUrl}/${url}${this.trailingSlash}`.replace(/([^:]\/)\/+/g, '$1');
  }

  private send(url: string, body?: Object, options?: Object): Promise<Response> {
    const requestObject: RequestInit = {
      method: 'GET', // *GET, POST, PUT, DELETE, etc.
      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/json',
        Authorization: this.authToken ? `Bearer ${this.authToken}` : this.authToken,
        'logrocket-session-id': window.logrocketSessionUrl,
        'app-location': this.getAppLocation(),
        'build-num': window.configs?.BUILD_NUMBER,
        'event-id': window.eventid,
        'event-user-id': window.eventuserid,
        referer: window.location.href,
        origin: window.location.origin,
        srot: 'event-app' // स्रोत source in hindi :P
      },
      referrerPolicy: 'no-referrer',
      ...(options || {}),
      body: (() => {
        if (!body) {
          return null;
        }
        if (typeof body === 'object') {
          return JSON.stringify(body);
        }
        return body;
      })()
    };
    const now = Date.now();
    const TenSec = 10 * 1000;
    if (now - this.lastTimeStamp > TenSec) {
      this.lastTimeStamp = now;
      this.callsInLast10Sec = 0;
    } else {
      this.callsInLast10Sec += 1;
    }
    // In the normal movement around the page I have seen this go upto 19 calls.
    // So setting it to thrice that number. If someone made more than 3times
    // the normal amount of calls in the last 10 sec we delay their calls.
    if (this.callsInLast10Sec > RATE_LIMIT_CALLS_PER_10_SEC) {
      loggerService.warn('[ApiClient] call rate limit exceeded, delaying request', {
        callsInLast10Sec: this.callsInLast10Sec,
        limit: RATE_LIMIT_CALLS_PER_10_SEC,
        delayMs: RATE_LIMIT_DELAY_MS,
        // Query string dropped: it carries ids and tokens. The path alone identifies the hot endpoint.
        endpoint: capDiagnosticString(String(url).split('?')[0])
      });
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          this.send(url, body, options).then(resolve).catch(reject);
        }, RATE_LIMIT_DELAY_MS);
      });
    }
    return fetch(url, requestObject);
  }

  private getAppLocation(): string {
    const pathname = window.location.pathname || '';
    let location = 'unknown'; // unknown
    if (pathname.includes('userhome')) {
      location = 'userhome';
    } else if (pathname.includes('/splash')) {
      location = 'splash';
    } else if (pathname.includes('/onboarding')) {
      location = 'onboarding';
    } else if (pathname.includes('/speakertechsetup')) {
      location = 'spkrtechsetup';
    } else if (pathname.includes('/agenda')) {
      location = 'agenda';
    } else if (pathname.includes('/discussiongroups')) {
      location = 'discgrp';
    } else if (pathname.includes('/speaker')) {
      location = 'speaker';
    } else if (pathname.includes('/network')) {
      location = 'network';
    } else if (pathname.includes('/discussion-room')) {
      location = 'discroom';
    } else if (pathname.includes('stage')) {
      location = 'stage';
    }
    return location;
  }

  private sendForJson(
    url: string,
    body?: Object,
    options?: Object,
    authRetries: number = 0,
    fetchFailedRetries: number = 0
  ): Promise<ApiResponse<any>> {
    return this.send(url, body, options)
      .then(res => {
        // Handling no content success response from backend
        if (res.status === 204) {
          return {
            res,
            data: {}
          };
        }
        return res
          .json()
          .then(data => {
            return {
              res,
              data
            };
          })
          .catch(err => {
            loggerService.error('error in sendForJson', err);
            throw new Error('Invalid api response! Expected parsable json!');
          });
      })
      .then(({ res, data }) => {
        if (res.ok) {
          return {
            statusCode: res.status,
            statusText: res.statusText,
            data
          };
        }

        if (res.status === 401) {
          if (authRetries < this.maxAuthRetries) {
            const forced = authRetries > 1;
            loggerService.warn('401 on API call, attempting token refresh', {
              url,
              authRetries,
              forced,
              error: data?.error
            });
            return this.onUnauthorized(forced, data?.error).then(() => {
              return this.sendForJson(url, body, options, authRetries + 1);
            });
          }
          loggerService.warn('401 persisted after max auth retries - giving up on this request', {
            url,
            maxAuthRetries: this.maxAuthRetries,
            error: data?.error
          });
          return Promise.reject({
            statusCode: res.status,
            statusText: res.statusText,
            data
          });
        }

        if (res.status === 429) {
          loggerService.warn('It looks like rate limiting kicked in! Delaying the call');

          if (!this.rateLimitedCalls[url]) {
            loggerService.warn('Rate limiting encountered for: ', url, body, options);
            this.rateLimitedCalls[url] = true;
            return new Promise<any>((resolve, reject) => {
              setTimeout(() => {
                loggerService.info('Retrying rate limited call after 10s');
                delete this.rateLimitedCalls[url];
                this.sendForJson(url, body, options).then(resolve).catch(reject);
              }, 10000);
            });
          }

          loggerService.warn('Already waiting for rate limited call of ', url, ' not scheduling another');
        }

        return Promise.reject({
          statusCode: res.status,
          statusText: res.statusText,
          data
        });
      })
      .catch(err => {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
          loggerService.warn('Call failed! Retrying in 5sec', url, err);
          if (fetchFailedRetries < this.maxFailedFetchRetries) {
            return new Promise(resolve => {
              setTimeout(() => {
                resolve(this.sendForJson(url, body, options, authRetries, fetchFailedRetries + 1));
              }, 5000);
            });
          }
          throw new Error(`Retried fetching ${fetchFailedRetries} times! Bailing!`);
        } else {
          loggerService.warn('CLient Error', err);
          throw err;
        }
      });
  }
}
